import { useEffect } from 'react'
import { useParams, useNavigate, useSearchParams, useOutletContext } from 'react-router-dom'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { ListRefreshIndicator } from '@/features/order-confirmation/components/ListRefreshIndicator'
import { workflowListQueryOptions } from '@/features/order-confirmation/workflowListQueries'
import { getWorkflowToDo } from '@/services/api'
import { Pagination } from '@/components/ui/Pagination'
import { relativeTime } from '@/lib/relativeTime'
import type { ToDoItem } from '@/types/workflowValidation'
import type { OverviewOutletContext } from './OverviewPage'

function Row({ item, onClick }: { item: ToDoItem; onClick: () => void }) {
  const isUnmatched = !item.purchase_order_id

  return (
    <tr
      className="border-t border-gray-100 transition-colors hover:bg-gray-50 cursor-pointer"
      onClick={onClick}
    >
      <td className="px-4 py-3">
        {isUnmatched ? (
          <>
            <p className="italic text-gray-400">Order not found</p>
            <p className="text-xs text-gray-400">
              {item.supplier_name || `${item.id}`}
            </p>
          </>
        ) : (
          <>
            {item.supplier_name
              ? <p className="font-medium text-gray-900">{item.supplier_name}</p>
              : <p className="text-gray-400">—</p>
            }
            {item.purchase_order_number
              ? <p className="text-xs text-gray-400">{item.purchase_order_number}</p>
              : null}
          </>
        )}
      </td>
      <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-700">
        {relativeTime(item.received)}
      </td>
      <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-700">
        {item.purchaser || <span className="text-gray-400">—</span>}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {item.number_of_rows > 0
          ? `${item.number_of_rows} rows`
          : <span className="text-gray-400">—</span>}
      </td>
    </tr>
  )
}

export function ToDoPage() {
  const { workflowId } = useParams<{ workflowId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { onPurchaserOptions } = useOutletContext<OverviewOutletContext>()

  const page = Number(searchParams.get('page')) || 1
  const selectedSuppliers = searchParams.getAll('supplier')
  const selectedPurchasers = searchParams.getAll('purchaser')
  const searchQuery = searchParams.get('q') ?? ''

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['workflow-todo', workflowId, page, selectedSuppliers, selectedPurchasers, searchQuery],
    queryFn: () =>
      getWorkflowToDo(workflowId!, {
        page,
        suppliers: selectedSuppliers.length > 0 ? selectedSuppliers : undefined,
        purchasers: selectedPurchasers.length > 0 ? selectedPurchasers : undefined,
        q: searchQuery || undefined,
      }),
    enabled: !!workflowId,
    placeholderData: keepPreviousData,
    ...workflowListQueryOptions,
  })

  const purchaserFilters = data?.purchaser_filters
  useEffect(() => {
    onPurchaserOptions(purchaserFilters ?? [])
  }, [purchaserFilters, onPurchaserOptions])

  const isBackgroundRefreshing = isFetching && !isLoading && !!data
  const items = data?.data ?? []
  const hasAnyFilter = selectedSuppliers.length > 0 || selectedPurchasers.length > 0 || !!searchQuery

  function handlePage(p: number) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (p > 1) next.set('page', String(p))
        else next.delete('page')
        return next
      },
      { replace: true }
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-400">
        Loading…
      </div>
    )
  }

  if (items.length === 0 && !hasAnyFilter) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-400">
        No order confirmations awaiting validation
      </div>
    )
  }

  return (
    <div className="relative">
      <ListRefreshIndicator visible={isBackgroundRefreshing} />

      <div className="data-sheet">
      <table className="w-full text-left">
        <thead>
          <tr className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            <th className="px-4 pb-2 pt-3">Supplier / Reference</th>
            <th className="hidden md:table-cell px-4 pb-2 pt-3">Received</th>
            <th className="hidden md:table-cell px-4 pb-2 pt-3">Purchaser</th>
            <th className="px-4 pb-2 pt-3">Rows</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-400">
                No results for current filters
              </td>
            </tr>
          ) : (
            items.map((item) => (
              <Row
                key={item.id}
                item={item}
                onClick={
                  item.purchase_order_id
                    ? () => navigate(`/workflow/${workflowId}/purchase-order/${item.purchase_order_id}`)
                    : () => navigate(`/workflow/${workflowId}/unmatched-oc/${item.id}`)
                }
              />
            ))
          )}
        </tbody>
      </table>
      </div>

      {data && (
        <Pagination
          page={data.page}
          totalCount={data.total}
          pageSize={data.page_size}
          onPage={handlePage}
        />
      )}
    </div>
  )
}
